import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Search, FolderKanban, ArrowRight, AlertTriangle, CheckCircle, Clock, Building2, MapPin, TrendingUp, TrendingDown } from 'lucide-react';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Input } from '@/components/ui/input';
import api from '@/lib/api';
import { toast } from 'sonner';

const RAG_COLORS = {
  red: { bg: 'bg-red-500/20', border: 'border-red-500/30', text: 'text-red-400', icon: AlertTriangle },
  amber: { bg: 'bg-amber-500/20', border: 'border-amber-500/30', text: 'text-amber-400', icon: Clock },
  green: { bg: 'bg-emerald-500/20', border: 'border-emerald-500/30', text: 'text-emerald-400', icon: CheckCircle }
};

export default function ProjectsList() {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterSector, setFilterSector] = useState('');
  const [filterRag, setFilterRag] = useState('');

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      const activeIds = JSON.parse(localStorage.getItem('ciq_active_projects') || '[]');
      const params = activeIds.length > 0 ? `?project_ids=${activeIds.join(',')}` : '';
      const res = await api.get(`/portfolios/portfolio_001/grid${params}`);
      setProjects(res.data.projects || []);
    } catch (err) {
      toast.error('Failed to load projects');
    } finally {
      setLoading(false);
    }
  };

  const filteredProjects = projects.filter(p => {
    const matchesSearch = !search || 
      p.name?.toLowerCase().includes(search.toLowerCase()) ||
      (typeof p.client === 'object' ? p.client?.clientName : p.client)?.toLowerCase().includes(search.toLowerCase());
    const matchesSector = !filterSector || p.sector === filterSector;
    const matchesRag = !filterRag || p.rag_status === filterRag;
    return matchesSearch && matchesSector && matchesRag;
  });

  const sectors = [...new Set(projects.map(p => p.sector))].filter(Boolean);

  const getSpiIcon = (spi) => {
    if (spi >= 1) return <TrendingUp className="w-3.5 h-3.5 text-emerald-400" />;
    return <TrendingDown className="w-3.5 h-3.5 text-red-400" />;
  };

  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-white">Projects</h1>
            <p className="text-gray-400 mt-1">{filteredProjects.length} of {projects.length} projects</p>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-3 mb-6">
          <div className="relative flex-1 min-w-[200px] max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
            <Input
              data-testid="projects-search"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search projects..."
              className="pl-10 bg-[#1F2937] border-[#3A3F4A] text-white"
            />
          </div>
          
          <select
            data-testid="filter-sector"
            value={filterSector}
            onChange={e => setFilterSector(e.target.value)}
            className="px-3 py-2 bg-[#1F2937] border border-[#3A3F4A] rounded-md text-white text-sm"
          >
            <option value="">All Sectors</option>
            {sectors.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          
          <select
            data-testid="filter-rag"
            value={filterRag}
            onChange={e => setFilterRag(e.target.value)}
            className="px-3 py-2 bg-[#1F2937] border border-[#3A3F4A] rounded-md text-white text-sm"
          >
            <option value="">All Status</option>
            <option value="green">Green</option>
            <option value="amber">Amber</option>
            <option value="red">Red</option>
          </select>
        </div>

        {/* Projects Grid */}
        {loading ? (
          <div className="text-center text-gray-400 py-12">Loading projects...</div>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {filteredProjects.map((project, index) => {
              const rag = RAG_COLORS[project.rag_status] || RAG_COLORS.green;
              const RagIcon = rag.icon;
              
              return (
                <motion.div
                  key={project.project_id}
                  data-testid={`project-card-${project.project_id}`}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  onClick={() => navigate(`/projects/${project.project_id}`)}
                  className="bg-[#0f1629] border border-[#3A3F4A] rounded-xl p-5 cursor-pointer hover:border-[#1E4E8C]/50 transition-all group"
                >
                  {/* Header */}
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-white truncate group-hover:text-[#1E4E8C] transition-colors">
                        {project.name}
                      </h3>
                      <p className="text-sm text-gray-500 truncate mt-0.5">{typeof project.client === 'object' ? project.client?.clientName : project.client}</p>
                    </div>
                    <div className={`p-2 rounded-lg ${rag.bg} ${rag.border} border`}>
                      <RagIcon className={`w-4 h-4 ${rag.text}`} />
                    </div>
                  </div>
                  
                  {/* Meta */}
                  <div className="flex items-center gap-4 text-xs text-gray-400 mb-4">
                    <span className="flex items-center gap-1">
                      <Building2 className="w-3.5 h-3.5" />
                      {project.sector}
                    </span>
                    {project.geography && (
                      <span className="flex items-center gap-1">
                        <MapPin className="w-3.5 h-3.5" />
                        {project.geography}
                      </span>
                    )}
                  </div>
                  
                  {/* Progress */}
                  <div className="mb-4">
                    <div className="flex justify-between text-xs mb-1.5">
                      <span className="text-gray-400">Progress</span>
                      <span className="text-white font-medium">{project.actual_pct?.toFixed(1) || 0}%</span>
                    </div>
                    <div className="h-2 bg-[#3A3F4A] rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-gradient-to-r from-[#1E4E8C] to-[#143A6B] rounded-full transition-all"
                        style={{ width: `${project.actual_pct || 0}%` }}
                      />
                    </div>
                  </div>
                  
                  {/* KPIs */}
                  <div className="grid grid-cols-3 gap-3 pt-3 border-t border-[#3A3F4A]">
                    <div>
                      <p className="text-[10px] text-gray-500 uppercase tracking-wider">SPI</p>
                      <div className="flex items-center gap-1">
                        {getSpiIcon(project.spi)}
                        <span className={`text-sm font-semibold ${project.spi >= 1 ? 'text-emerald-400' : 'text-red-400'}`}>
                          {project.spi?.toFixed(2) || '-'}
                        </span>
                      </div>
                    </div>
                    <div>
                      <p className="text-[10px] text-gray-500 uppercase tracking-wider">CPI</p>
                      <span className={`text-sm font-semibold ${project.cpi >= 1 ? 'text-emerald-400' : 'text-red-400'}`}>
                        {project.cpi?.toFixed(2) || '-'}
                      </span>
                    </div>
                    <div>
                      <p className="text-[10px] text-gray-500 uppercase tracking-wider">Health</p>
                      <span className="text-sm font-semibold text-white">{project.hi_score || '-'}</span>
                    </div>
                  </div>
                  
                  {/* View Button */}
                  <div className="mt-4 pt-3 border-t border-[#3A3F4A] flex justify-end">
                    <span className="text-xs text-[#1E4E8C] flex items-center gap-1 group-hover:gap-2 transition-all">
                      View Dashboard <ArrowRight className="w-3.5 h-3.5" />
                    </span>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
        
        {!loading && filteredProjects.length === 0 && (
          <div className="text-center py-16">
            <FolderKanban className="w-16 h-16 text-gray-600 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-white mb-2">No projects found</h3>
            <p className="text-gray-400">
              {search || filterSector || filterRag 
                ? 'Try adjusting your filters'
                : 'Projects will appear here once added'
              }
            </p>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
